import "./App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RefTracker from "./components/RefTracker";
import { StepUpProvider } from "./components/StepUpGuard";
import { MfaGate } from "./components/MfaGate";
import { AAHeader, AAFooter } from "./components/aa/AAShell";
import GlobalLinkStrip from "./components/aa/GlobalLinkStrip";
import { detectBrand } from "./lib/brand";
import { primeAirportBannerOverrides } from "./lib/banner";
import axios from "axios";

// Eager: the landing page (fast first paint) + 404
import AAHome from "./pages/AAHome";
import NotFound from "./pages/NotFound";

// Everything else is code-split — each page's chunk loads only when visited.
const AskTravelPulse = lazy(() => import("./components/AskTravelPulse"));
const CategoryHub = lazy(() => import("./pages/CategoryHub"));
const Rail = lazy(() => import("./pages/Rail"));
const RailStation = lazy(() => import("./pages/RailStation"));
const RailOperator = lazy(() => import("./pages/RailOperator"));
const TransportVertical = lazy(() => import("./pages/TransportVertical"));
const TransportHubPage = lazy(() => import("./pages/TransportDetail").then((m) => ({ default: m.TransportHubPage })));
const TransportOperatorPage = lazy(() => import("./pages/TransportDetail").then((m) => ({ default: m.TransportOperatorPage })));
const VisasHub = lazy(() => import("./pages/VisasHub"));
const VisaCountryDetail = lazy(() => import("./pages/VisaCountryDetail"));
const MissionProfile = lazy(() => import("./pages/MissionProfile"));
const RegionMissions = lazy(() => import("./pages/RegionMissions"));
const AboutFounder = lazy(() => import("./pages/AboutFounder"));
const OurStory = lazy(() => import("./pages/OurStory"));
const ArticlesIndex = lazy(() => import("./pages/ArticlesIndex"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const BusinessDetail = lazy(() => import("./pages/BusinessDetail"));
const MyBusiness = lazy(() => import("./pages/MyBusiness"));
const ArticleView = lazy(() => import("./pages/ArticleView"));
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const Countries = lazy(() => import("./pages/Countries"));
const CountryDetail = lazy(() => import("./pages/CountryDetail"));
const StateDetail = lazy(() => import("./pages/StateDetail"));
const Legal = lazy(() => import("./pages/Legal"));
const City = lazy(() => import("./pages/City"));
const TravelHub = lazy(() => import("./pages/TravelHub"));
const TripPlannerAI = lazy(() => import("./pages/TripPlannerAI"));
const TripToday = lazy(() => import("./pages/TripToday"));
const GetApp = lazy(() => import("./pages/GetApp"));
const IndiaAirportsHome = lazy(() => import("./pages/IndiaAirportsHome"));
const Airports = lazy(() => import("./pages/Airports"));
const AirportsDirectory = lazy(() => import("./pages/AirportsDirectory"));
const AirportDetail = lazy(() => import("./pages/AirportDetail"));
const Airlines = lazy(() => import("./pages/Airlines"));
const AirlinesDirectory = lazy(() => import("./pages/AirlinesDirectory"));
const AirlineDetail = lazy(() => import("./pages/AirlineDetail"));
const AASearch = lazy(() => import("./pages/AASearch"));
const RoutePage = lazy(() => import("./pages/RoutePage"));
const AdminAviation = lazy(() => import("./pages/AdminAviation"));
const AdminAirportBanners = lazy(() => import("./pages/AdminAirportBanners"));
const AdminAiQueue = lazy(() => import("./pages/AdminAiQueue"));
const GuideAiPage = lazy(() => import("./pages/GuideAiPage"));
const IndiaAirports = lazy(() => import("./pages/IndiaAirports"));
const IndiaAirportDetail = lazy(() => import("./pages/IndiaAirportDetail"));
const IndiaAirlines = lazy(() => import("./pages/IndiaAirlines"));
const IndiaAirlineDetail = lazy(() => import("./pages/IndiaAirlineDetail"));
const IndiaRoutePage = lazy(() => import("./pages/IndiaRoutePage"));
const IndiaStatePage = lazy(() => import("./pages/IndiaStatePage"));
const AirportTerminalsPage = lazy(() => import("./pages/AirportTerminalsPage"));
const AirportLoungesPage = lazy(() => import("./pages/AirportLoungesPage"));
const TravelToIndia = lazy(() => import("./pages/TravelToIndia"));
const TravelFestivals = lazy(() => import("./pages/TravelFestivals"));
const AdminControl = lazy(() => import("./pages/AdminControl"));
const IndiaAbout = lazy(() => import("./pages/IndiaAbout"));
const IndiaMyFlight = lazy(() => import("./pages/IndiaMyFlight"));
const IndiaOciGuide = lazy(() => import("./pages/IndiaOciGuide"));
const IndiaVisitingGuide = lazy(() => import("./pages/IndiaVisitingGuide"));
const IndiaGuidesIndex = lazy(() => import("./pages/IndiaGuidesIndex"));
const IndiaPartners = lazy(() => import("./pages/IndiaPartners"));
const IndiaPlannerWidget = lazy(() => import("./pages/IndiaPlannerWidget"));
const RemindMe = lazy(() => import("./pages/RemindMe"));
const GearInventory = lazy(() => import("./pages/GearInventory"));
const TripDetail = lazy(() => import("./pages/TripDetail"));
const TripDiary = lazy(() => import("./pages/TripDiary"));
const ExpenseManager = lazy(() => import("./pages/ExpenseManager"));
const StoryWizard = lazy(() => import("./pages/StoryWizard"));
const StoryStudio = lazy(() => import("./pages/StoryStudio"));
const MyStories = lazy(() => import("./pages/MyStories"));
const PublicStory = lazy(() => import("./pages/PublicStory"));
const OurCochin = lazy(() => import("./pages/OurCochin"));
const TheChenda = lazy(() => import("./pages/TheChenda"));
const KeralaAyurveda = lazy(() => import("./pages/KeralaAyurveda"));
const Specials = lazy(() => import("./pages/Specials"));
const PhotoMap = lazy(() => import("./pages/PhotoMap"));
const MyPhotos = lazy(() => import("./pages/MyPhotos"));
const TravelDay = lazy(() => import("./pages/TravelDay"));
const TripShareView = lazy(() => import("./pages/TripShareView"));
const Memories = lazy(() => import("./pages/Memories"));
const Membership = lazy(() => import("./pages/Membership"));
const GroupTripPlanner = lazy(() => import("./pages/GroupTripPlanner"));
const TripExpenseManagerPage = lazy(() => import("./pages/TripExpenseManagerPage"));
const FamilyExpenseHubPage = lazy(() => import("./pages/FamilyExpenseHubPage"));
const MySocialPulse = lazy(() => import("./pages/MySocialPulse"));
const MySocialPulseArticle = lazy(() => import("./pages/MySocialPulseArticle"));
const FoodShopping = lazy(() => import("./pages/FoodShopping"));
const PantryPage = lazy(() => import("./pages/PantryPage"));
const AdminPulseSources = lazy(() => import("./pages/AdminPulseSources"));
const DocumentsLocker = lazy(() => import("./pages/DocumentsLocker"));
const FamilyAccept = lazy(() => import("./pages/FamilyAccept"));
const TravelHubLanding = lazy(() => import("./pages/TravelHubLanding"));
const AppStart = lazy(() => import("./pages/AppStart"));
const SharedExpenses = lazy(() => import("./pages/SharedExpenses"));
const EmbedGallery = lazy(() => import("./pages/EmbedGallery"));
const CountdownWidget = lazy(() => import("./pages/CountdownWidget"));
const SupportUs = lazy(() => import("./pages/SupportUs"));
const Feedback = lazy(() => import("./pages/Feedback"));
const HouseholdAccept = lazy(() => import("./pages/HouseholdAccept"));
const PaymentSuccess = lazy(() => import("./pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("./pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const MapExplorer = lazy(() => import("./pages/MapExplorer"));
const Aircraft = lazy(() => import("./pages/Aircraft"));
const AircraftDetail = lazy(() => import("./pages/AircraftDetail"));
const Manufacturers = lazy(() => import("./pages/Manufacturers"));
const ManufacturerDetail = lazy(() => import("./pages/ManufacturerDetail"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const LoyaltyExplore = lazy(() => import("./pages/LoyaltyExplore"));
const Hotels = lazy(() => import("./pages/Hotels"));
const AdminAffiliates = lazy(() => import("./pages/AdminAffiliates"));
const AdminCoupons = lazy(() => import("./pages/AdminCoupons"));
const AdminFares = lazy(() => import("./pages/AdminFares"));
const AdminRefTraffic = lazy(() => import("./pages/AdminRefTraffic"));
const Features = lazy(() => import("./pages/Features"));
const AdminAnswers = lazy(() => import("./pages/AdminAnswers"));
const AdminVisaRequirements = lazy(() => import("./pages/AdminVisaRequirements"));
const Faq = lazy(() => import("./pages/Faq"));
const PackingChecklist = lazy(() => import("./pages/PackingChecklist"));
const FlightSearch = lazy(() => import("./pages/FlightSearch"));
const BaggageFees = lazy(() => import("./pages/BaggageFees"));
const PetTravel = lazy(() => import("./pages/PetTravel"));
const FareComparison = lazy(() => import("./pages/FareComparison"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const Advertise = lazy(() => import("./pages/Advertise"));
const Press = lazy(() => import("./pages/Press"));
const AdminAdvertising = lazy(() => import("./pages/AdminAdvertising"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const Community = lazy(() => import("./pages/Community"));
const CommunityPost = lazy(() => import("./pages/CommunityPost"));
const SavedCommunityPosts = lazy(() => import("./pages/SavedCommunityPosts"));
const Memoirs = lazy(() => import("./pages/Memoirs"));
const SharedMemoir = lazy(() => import("./pages/SharedMemoir"));
const TravelDiariesList = lazy(() => import("./pages/TravelDiariesList"));
const FoodDining = lazy(() => import("./pages/FoodDining"));
const SharedFoodList = lazy(() => import("./pages/SharedFoodList"));
const StayGuide = lazy(() => import("./pages/StayGuide"));
const BusinessSubmit = lazy(() => import("./pages/BusinessSubmit"));
const AdminIntelligence = lazy(() => import("./pages/AdminIntelligence"));
const OfficialsApply = lazy(() => import("./pages/OfficialsApply"));
const AdminOfficials = lazy(() => import("./pages/AdminOfficials"));
const Complaints = lazy(() => import("./pages/Complaints"));
const FlightRecovery = lazy(() => import("./pages/FlightRecovery"));
const PriceWatch = lazy(() => import("./pages/PriceWatch"));
const AdminTravelCommerce = lazy(() => import("./pages/AdminTravelCommerce"));
const ExploreGuide = lazy(() => import("./pages/ExploreGuide"));
const ExplorePlaces = lazy(() => import("./pages/ExplorePlaces"));
const TopDestinations = lazy(() => import("./pages/TopDestinations"));
const AviationNews = lazy(() => import("./pages/AviationNews"));
const TravelNews = lazy(() => import("./pages/TravelNews"));
const MyDealAlerts = lazy(() => import("./pages/MyDealAlerts"));
const IndiaCities = lazy(() => import("./pages/IndiaCities"));
const UsCities = lazy(() => import("./pages/UsCities"));
const UkCities = lazy(() => import("./pages/UkCities"));
const UaeCities = lazy(() => import("./pages/UaeCities"));
const AuCities = lazy(() => import("./pages/AuCities"));
const WorldCities = lazy(() => import("./pages/WorldCities"));
const CityGuidesHub = lazy(() => import("./pages/CityGuidesHub"));
const AppLogin = lazy(() => import("./pages/AppLogin"));
const SocialPulseCallback = lazy(() => import("./pages/SocialPulseCallback"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const MobileAuthHandshake = lazy(() => import("./pages/MobileAuthHandshake"));
const AdminGeoContent = lazy(() => import("./pages/AdminGeoContent"));
const ComplaintDetail = lazy(() => import("./pages/ComplaintDetail"));
const NewComplaint = lazy(() => import("./pages/NewComplaint"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthComplete = lazy(() => import("./pages/AuthComplete"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const PrivacySecurity = lazy(() => import("./pages/PrivacySecurity"));
const SecurityPrivacy = lazy(() => import("./pages/SecurityPrivacy"));
const MyTravelHubGuide = lazy(() => import("./pages/MyTravelHubGuide"));
const SharedDoc = lazy(() => import("./pages/SharedDoc"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Moderation = lazy(() => import("./pages/Moderation"));

// Scroll to the top of the page on every route change (footer links, in-page links, etc.)
// If the URL contains a #hash, scroll to that element instead (retry briefly while content loads).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.slice(1);
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
      if (tries++ < 20) setTimeout(attempt, 150);
    };
    attempt();
  }, [pathname, hash]);
  return null;
}

// SEO redirects for legacy /india/* URLs → root-level paths (guides, my-flight, etc.)
function IndiaLegacyRedirect() {
  const { pathname, search } = useLocation();
  let p = pathname.replace(/^\/india/, "");
  if (p === "" || p === "/") p = "/india";
  return <Navigate to={p + search} replace />;
}

// The 44 enriched IndianAirports IATA codes keep their deep India pages at /airports/{code}
const INDIA_IATA = new Set(["AMD", "ATQ", "BBI", "BDQ", "BHO", "BLR", "BOM", "CCJ", "CCU", "CJB",
  "COK", "DBR", "DED", "DEL", "GAU", "GOI", "GOX", "HBX", "HYD", "IDR", "IMF", "IXA", "IXC",
  "IXE", "IXL", "IXM", "IXR", "IXZ", "JAI", "JDH", "KNU", "LKO", "MAA", "NAG", "PAT", "PNQ",
  "STV", "SXR", "TIR", "TRV", "UDR", "VGA", "VNS", "VTZ"]);

// /airports/:slug — Indian slugs & enriched India IATA → India page, global codes → /airport/{code}
function AirportParamDispatch() {
  const { slug } = useParams();
  const s = (slug || "").trim();
  const up = s.toUpperCase();
  if (!s.includes("-") && s.length <= 5 && !INDIA_IATA.has(up)) return <Navigate to={`/airport/${up}`} replace />;
  return <IndiaAirportDetail />;
}

// /airlines/:slug — numeric ids / 2-letter IATA → global page, name slugs → India page
function AirlineParamDispatch() {
  const { slug } = useParams();
  const s = (slug || "").trim();
  if (/^\d+$/.test(s) || s.length <= 2) return <Navigate to={`/airline/${s}`} replace />;
  return <IndiaAirlineDetail />;
}

// Canonical /airport/{name-slug}-{IATA} accepted — trailing code wins
function GlobalAirportCanonical() {
  const { code } = useParams();
  if (code.includes("-")) return <Navigate to={`/airport/${code.split("-").pop().toUpperCase()}`} replace />;
  return <AirportDetail />;
}

function GlobalAirlineCanonical() {
  const { code } = useParams();
  if (code.includes("-")) return <Navigate to={`/airline/${code.split("-").pop().toUpperCase()}`} replace />;
  return <AirlineDetail />;
}

// Hostname-aware root: indianairports.com → India home, everywhere else → global AAHome
function HomeDispatch() {
  return detectBrand().isIndia ? <IndiaAirportsHome /> : <AAHome />;
}

const PageLoader = () => (
  <div className="flex items-center justify-center py-24" data-testid="page-loader">
    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-700 rounded-full animate-spin" />
  </div>
);

function Layout() {
  const { pathname, search } = useLocation();
  const travelDayMode = pathname.startsWith("/travel-day/"); // distraction-free screen: no site chrome
  // Embeddable pages (partner sites iframe them with ?embed=1): hide all chrome
  const embedMode = (pathname.startsWith("/trip-expenses/shared/") || pathname.startsWith("/widgets/"))
    && new URLSearchParams(search).get("embed") === "1";
  const bare = travelDayMode || embedMode;
  // My Social Pulse app-shell: hide footer
  const pulseMode = pathname.startsWith("/my-social-pulse") || pathname.startsWith("/locker");
  // Hostname-aware default title / meta so the india domain reads as IndianAirports
  useEffect(() => {
    const brand = detectBrand();
    if (brand.isIndia && !document.title.toLowerCase().includes("indianairports")) {
      document.title = `${brand.domain} — ${brand.tagline}`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content",
        `${brand.domain} — the definitive guide to Indian airports, airlines, routes and travel information.`);
    }
  }, [pathname]);
  // Prime airport-banner overrides once on app boot.
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/airport-banners`)
      .then((r) => primeAirportBannerOverrides(r.data?.items || []))
      .catch(() => {});
  }, []);
  return (
    <div className="App flex flex-col min-h-screen bg-white">
      <ScrollToTop />
      {!bare && <GlobalLinkStrip />}
      {!bare && <AAHeader />}
      <main className="flex-1"><Suspense fallback={<PageLoader />}><Outlet /></Suspense></main>
      {!bare && !pulseMode && <AAFooter />}
      {!bare && <Suspense fallback={null}><AskTravelPulse /></Suspense>}
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route element={<Layout />}>
        {/* AirlinesAirports.com core (global, root-level) */}
        <Route path="/" element={<HomeDispatch />} />
        <Route path="/search" element={<AASearch />} />
        <Route path="/airports" element={<Airports />} />
        <Route path="/airports/directory" element={<AirportsDirectory />} />
        <Route path="/airports/ranking" element={<AirportsDirectory />} />
        <Route path="/airports/routes/:pair" element={<IndiaRoutePage />} />
        <Route path="/airports/states/:slug" element={<IndiaStatePage />} />
        <Route path="/airports/:slug/terminals" element={<AirportTerminalsPage />} />
        <Route path="/airports/:slug/lounges" element={<AirportLoungesPage />} />
        <Route path="/travel-to-india" element={<TravelToIndia />} />
        <Route path="/travel-to-india/festivals" element={<TravelFestivals />} />
        <Route path="/admin/control" element={<AdminControl />} />
        <Route path="/airports/:slug" element={<AirportParamDispatch />} />
        <Route path="/airport/:code" element={<GlobalAirportCanonical />} />
        <Route path="/airlines" element={<Airlines />} />
        <Route path="/airlines/directory" element={<AirlinesDirectory />} />
        <Route path="/airlines/ranking" element={<AirlinesDirectory />} />
        <Route path="/airlines/:slug" element={<AirlineParamDispatch />} />
        <Route path="/airline/:code" element={<GlobalAirlineCanonical />} />
        <Route path="/route/:pair" element={<RoutePage />} />
        <Route path="/admin/aviation" element={<ProtectedRoute moderator><AdminAviation /></ProtectedRoute>} />
        <Route path="/admin/airport-banners" element={<ProtectedRoute moderator><AdminAirportBanners /></ProtectedRoute>} />
        <Route path="/admin/ai-queue" element={<ProtectedRoute moderator><AdminAiQueue /></ProtectedRoute>} />
        {/* IndianAirports platform section */}
        <Route path="/india" element={<IndiaAirportsHome />} />
        <Route path="/india/airports" element={<IndiaAirports />} />
        <Route path="/india/airlines" element={<IndiaAirlines />} />
        <Route path="/about" element={<IndiaAbout />} />
        <Route path="/my-flight" element={<IndiaMyFlight />} />
        <Route path="/guides" element={<IndiaGuidesIndex />} />
        <Route path="/guides/oci-nri-rules" element={<IndiaOciGuide />} />
        <Route path="/guides/visiting-india" element={<IndiaVisitingGuide />} />
        <Route path="/guides/topic/:slug" element={<GuideAiPage />} />
        <Route path="/partners" element={<IndiaPartners />} />
        {/* Legacy /india/* deep URLs → root-level redirects */}
        <Route path="/india/*" element={<IndiaLegacyRedirect />} />
        {/* Shared platform features */}
        <Route path="/air" element={<CategoryHub category="air" />} />
        <Route path="/rail" element={<Rail />} />
        <Route path="/rail/stations/:id" element={<RailStation />} />
        <Route path="/rail/operators/:id" element={<RailOperator />} />
        <Route path="/bus" element={<TransportVertical mode="bus" />} />
        <Route path="/ground" element={<TransportVertical mode="ground" />} />
        <Route path="/rideshare" element={<TransportVertical mode="rideshare" />} />
        <Route path="/road" element={<TransportVertical mode="road" />} />
        <Route path="/water" element={<TransportVertical mode="water" />} />
        <Route path="/stays" element={<TransportVertical mode="stay" />} />
        <Route path="/t/:mode/hubs/:id" element={<TransportHubPage />} />
        <Route path="/t/:mode/operators/:id" element={<TransportOperatorPage />} />
        <Route path="/visas" element={<VisasHub />} />
        <Route path="/visas/missions/:id" element={<MissionProfile />} />
        <Route path="/visas/regions/:host/:region" element={<RegionMissions />} />
        <Route path="/visas/:id" element={<VisaCountryDetail />} />
        <Route path="/features" element={<Features />} />
        <Route path="/about/founder" element={<AboutFounder />} />
        <Route path="/explore-plan-share-travel" element={<OurStory />} />
        <Route path="/articles" element={<ArticlesIndex />} />
        <Route path="/articles/special/our-cochin" element={<OurCochin />} />
        <Route path="/articles/special/the-chenda" element={<TheChenda />} />
        <Route path="/articles/special/kerala-ayurveda" element={<KeralaAyurveda />} />
        <Route path="/specials" element={<Specials />} />
        <Route path="/articles/:slug" element={<ArticleView />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/business/:id" element={<BusinessDetail />} />
        <Route path="/my-business" element={<MyBusiness />} />
        <Route path="/destinations" element={<Destinations />} />
        <Route path="/destinations/:id" element={<DestinationDetail />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/countries/:code" element={<CountryDetail />} />
        <Route path="/regions/:code" element={<StateDetail />} />
        <Route path="/legal/:slug" element={<Legal />} />
        <Route path="/cities/:name" element={<City />} />
        <Route path="/map" element={<MapExplorer />} />
        <Route path="/aircraft" element={<Aircraft />} />
        <Route path="/aircraft/:code" element={<AircraftDetail />} />
        <Route path="/manufacturers" element={<Manufacturers />} />
        <Route path="/manufacturers/:name" element={<ManufacturerDetail />} />
        <Route path="/news" element={<Articles />} />
        <Route path="/news/:slug" element={<ArticleDetail />} />
        <Route path="/loyalty" element={<LoyaltyExplore />} />
        <Route path="/hotels" element={<Hotels />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/post/:pid" element={<CommunityPost />} />
        <Route path="/community/saved" element={<ProtectedRoute><SavedCommunityPosts /></ProtectedRoute>} />
        <Route path="/memoirs" element={<ProtectedRoute><Memoirs /></ProtectedRoute>} />
        <Route path="/memoirs/shared/:token" element={<SharedMemoir />} />
        <Route path="/travel-diaries" element={<ProtectedRoute><TravelDiariesList /></ProtectedRoute>} />
        <Route path="/officials/apply" element={<ProtectedRoute><OfficialsApply /></ProtectedRoute>} />
        <Route path="/admin/officials" element={<ProtectedRoute moderator><AdminOfficials /></ProtectedRoute>} />
        <Route path="/admin/affiliates" element={<ProtectedRoute moderator><AdminAffiliates /></ProtectedRoute>} />
        <Route path="/admin/travel" element={<ProtectedRoute moderator><AdminTravelCommerce /></ProtectedRoute>} />
        <Route path="/admin/geo" element={<ProtectedRoute moderator><AdminGeoContent /></ProtectedRoute>} />
        <Route path="/admin/coupons" element={<ProtectedRoute moderator><AdminCoupons /></ProtectedRoute>} />
        <Route path="/admin/fares" element={<ProtectedRoute moderator><AdminFares /></ProtectedRoute>} />
        <Route path="/admin/ref-traffic" element={<ProtectedRoute moderator><AdminRefTraffic /></ProtectedRoute>} />
        <Route path="/admin/answers" element={<ProtectedRoute moderator><AdminAnswers /></ProtectedRoute>} />
        <Route path="/admin/visa-requirements" element={<ProtectedRoute moderator><AdminVisaRequirements /></ProtectedRoute>} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/packing" element={<PackingChecklist />} />
        <Route path="/flight-search" element={<FlightSearch />} />
        <Route path="/baggage" element={<BaggageFees />} />
        <Route path="/pet-travel" element={<PetTravel />} />
        <Route path="/fares" element={<FareComparison />} />
        <Route path="/admin/feedback" element={<ProtectedRoute moderator><AdminFeedback /></ProtectedRoute>} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/press" element={<Press />} />
        <Route path="/admin/advertising" element={<ProtectedRoute moderator><AdminAdvertising /></ProtectedRoute>} />
        <Route path="/complaints" element={<Complaints />} />
        <Route path="/recovery" element={<FlightRecovery />} />
        <Route path="/price-watch" element={<PriceWatch />} />
        <Route path="/food-dining/*" element={<FoodDining />} />
        <Route path="/food-list/shared/:token" element={<SharedFoodList />} />
        <Route path="/hotels-accommodation/*" element={<StayGuide />} />
        <Route path="/submit-business" element={<BusinessSubmit />} />
        <Route path="/admin/intelligence" element={<ProtectedRoute moderator><AdminIntelligence /></ProtectedRoute>} />
        <Route path="/explore/*" element={<ExploreGuide />} />
        <Route path="/explore-places" element={<ExplorePlaces />} />
        <Route path="/top-destinations" element={<TopDestinations />} />
        <Route path="/travel-news" element={<TravelNews />} />
        <Route path="/aviation-news" element={<AviationNews />} />
        <Route path="/my-deal-alerts" element={<ProtectedRoute><MyDealAlerts /></ProtectedRoute>} />
        <Route path="/india-cities" element={<IndiaCities />} />
        <Route path="/us-cities" element={<UsCities />} />
        <Route path="/uk-cities" element={<UkCities />} />
        <Route path="/uae-cities" element={<UaeCities />} />
        <Route path="/au-cities" element={<AuCities />} />
        <Route path="/world-cities/:key" element={<WorldCities />} />
        <Route path="/city-guides" element={<CityGuidesHub />} />
        <Route path="/complaints/new" element={<ProtectedRoute><NewComplaint /></ProtectedRoute>} />
        <Route path="/complaints/:cid" element={<ComplaintDetail />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/app-login" element={<AppLogin />} />
        <Route path="/auth/mobile" element={<MobileAuthHandshake />} />
        <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
        <Route path="/auth/callback" element={<SocialPulseCallback />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/register" element={<Auth />} />
        <Route path="/auth/complete" element={<AuthComplete />} />
        <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
        <Route path="/privacy-security" element={<ProtectedRoute><PrivacySecurity /></ProtectedRoute>} />
        <Route path="/security-privacy" element={<SecurityPrivacy />} />
        <Route path="/my-travel-hub-guide" element={<MyTravelHubGuide />} />
        <Route path="/shared-doc/:token" element={<SharedDoc />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/travel-hub" element={<TravelHub />} />
        <Route path="/grouptrip-planner" element={<GroupTripPlanner />} />
        <Route path="/trip-expense-manager" element={<TripExpenseManagerPage />} />
        <Route path="/family-expense-hub" element={<FamilyExpenseHubPage />} />
        <Route path="/my-social-pulse" element={<ProtectedRoute><MySocialPulse /></ProtectedRoute>} />
        <Route path="/my-social-pulse/food" element={<ProtectedRoute><FoodShopping /></ProtectedRoute>} />
        <Route path="/my-social-pulse/pantry" element={<ProtectedRoute><PantryPage /></ProtectedRoute>} />
        <Route path="/admin/pulse-sources" element={<ProtectedRoute moderator><AdminPulseSources /></ProtectedRoute>} />
        <Route path="/locker" element={<ProtectedRoute><DocumentsLocker /></ProtectedRoute>} />
        <Route path="/family/accept" element={<FamilyAccept />} />
        <Route path="/travel-hub-landing" element={<TravelHubLanding />} />
        <Route path="/app-start" element={<AppStart />} />
        <Route path="/trip-expenses/shared/:token" element={<SharedExpenses />} />
        <Route path="/embed" element={<EmbedGallery />} />
        <Route path="/widgets/countdown/:token" element={<CountdownWidget />} />
        <Route path="/widgets/india-planner" element={<IndiaPlannerWidget />} />
        <Route path="/plan-ai" element={<TripPlannerAI />} />
        <Route path="/trip-today" element={<TripToday />} />
        <Route path="/get-app" element={<GetApp />} />
        <Route path="/social-pulse-app" element={<MySocialPulseArticle />} />
        <Route path="/remind-me" element={<RemindMe />} />
        <Route path="/gear" element={<GearInventory />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/MyTravelHub" element={<Membership />} />
        <Route path="/mytravelhub" element={<Membership />} />
        <Route path="/support" element={<SupportUs />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/household/accept" element={<HouseholdAccept />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/trips/:id" element={<ProtectedRoute><TripDetail /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><ExpenseManager /></ProtectedRoute>} />
        <Route path="/trips/:id/diary" element={<ProtectedRoute><TripDiary /></ProtectedRoute>} />
        <Route path="/trips/:id/create-story" element={<ProtectedRoute><StoryWizard /></ProtectedRoute>} />
        <Route path="/stories/:sid/edit" element={<ProtectedRoute><StoryStudio /></ProtectedRoute>} />
        <Route path="/my-stories" element={<ProtectedRoute><MyStories /></ProtectedRoute>} />
        <Route path="/story/:publicId" element={<PublicStory />} />
        <Route path="/photo-map" element={<ProtectedRoute><PhotoMap /></ProtectedRoute>} />
        <Route path="/my-photos" element={<ProtectedRoute><MyPhotos /></ProtectedRoute>} />
        <Route path="/travel-day/:id" element={<ProtectedRoute><TravelDay /></ProtectedRoute>} />
        <Route path="/trip-share/:token" element={<TripShareView />} />
        <Route path="/memories/:token" element={<Memories />} />
        <Route path="/moderation" element={<ProtectedRoute moderator><Moderation /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RefTracker />
        <StepUpProvider>
          <Toaster position="bottom-right" richColors offset={110} mobileOffset={80} />
          <MfaGate />
          <AppRouter />
        </StepUpProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
